import React, { useState, useEffect } from 'react';
import { X, User, LogOut, Shield, Wrench, Clock, BarChart3, Award, ClipboardCheck, ClipboardList, BookOpen, ExternalLink, AlertTriangle, Users } from 'lucide-react';
import { getActiveSlots } from './BottomNav';
import { API_URL, apiFetch } from './config/api';

function SlideUpMenu({ isOpen, onClose, onMenuItemClick, isAdmin, isSuperAdmin, user, hasPermission, tenant }) {
  const [activeSlots, setActiveSlots] = useState(getActiveSlots());
  const [externalLinks, setExternalLinks] = useState([]);
  const [externalLinkWarning, setExternalLinkWarning] = useState(null); // { title, url }

  // Listen for slot config changes AND window resize (mobile/desktop switch)
  useEffect(() => {
    const handleConfigChange = () => {
      setActiveSlots(getActiveSlots());
    };
    window.addEventListener('bottomNavConfigChanged', handleConfigChange);
    window.addEventListener('storage', handleConfigChange);
    window.addEventListener('resize', handleConfigChange);
    return () => {
      window.removeEventListener('bottomNavConfigChanged', handleConfigChange);
      window.removeEventListener('storage', handleConfigChange);
      window.removeEventListener('resize', handleConfigChange);
    };
  }, []);

  // Fetch external links when menu opens
  useEffect(() => {
    if (isOpen) {
      const fetchExternalLinks = async () => {
        try {
          const res = await apiFetch(`${API_URL}/settings/external-links.php`);
          const data = await res.json();
          if (data.success) {
            setExternalLinks(data.links || []);
          }
        } catch (error) {
          console.error('Failed to load external links:', error);
        }
      };
      fetchExternalLinks();
    }
  }, [isOpen]);

  // Handle external link click - show warning
  const handleExternalLinkClick = (link) => {
    setExternalLinkWarning({ title: link.title, url: link.url, icon: link.icon });
  };

  // Confirm and open external link
  const confirmExternalLink = () => {
    if (externalLinkWarning) {
      window.open(externalLinkWarning.url, '_blank', 'noopener,noreferrer');
      setExternalLinkWarning(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  // Items that are shown in bottom nav should be hidden from hamburger menu
  const hiddenItems = activeSlots;

  // Check permissions
  const hasAdminPermissions = hasPermission && (
    hasPermission('users.view') ||
    hasPermission('users.invite') ||
    hasPermission('roles.view') ||
    hasPermission('roles.create') ||
    hasPermission('roles.edit') ||
    hasPermission('roles.delete') ||
    hasPermission('trainings.create') ||
    hasPermission('trainings.assign') ||
    hasPermission('certifications.view_all') ||
    hasPermission('templates.manage')
  );

  const canAccessAdminPanel = isAdmin || isSuperAdmin || hasAdminPermissions;
  const canAccessRepairs = isAdmin || isSuperAdmin || (hasPermission && hasPermission('repairs.view'));
  const canAccessHourReporting = isAdmin || isSuperAdmin || (hasPermission && hasPermission('hours.view'));
  const canAccessReports = isAdmin || isSuperAdmin || (hasPermission && hasPermission('reports.view'));
  const canAccessDiary = isAdmin || isSuperAdmin || (hasPermission && hasPermission('diary.view'));

  // All possible menu items (will be filtered based on what's in bottom nav)
  const allMenuItems = [
    // Profile - always visible
    {
      id: 'profile',
      icon: User,
      label: 'Profiili',
      subtitle: user?.email || '',
      action: () => onMenuItemClick('profile'),
      alwaysShow: true
    },
    // Tehtävät
    {
      id: 'tasks',
      icon: ClipboardList,
      label: 'Tehtävät',
      subtitle: 'Projektit ja tehtävät',
      action: () => onMenuItemClick('tasks')
    },
    // Kortit
    {
      id: 'training',
      icon: Award,
      label: 'Omat Sertifikaatit',
      subtitle: 'Kortit ja pätevyydet',
      action: () => onMenuItemClick('training')
    },
    // QMS / Laatu (only if feature enabled or superadmin)
    ...((isSuperAdmin || tenant?.feature_qms) ? [{
      id: 'qms',
      icon: ClipboardCheck,
      label: 'Laadunhallinta',
      subtitle: 'Poikkeamat ja dokumentit',
      action: () => onMenuItemClick('qms')
    }] : []),
    // Repairs (conditional)
    ...(canAccessRepairs ? [{
      id: 'repairs',
      icon: Wrench,
      label: 'Korjaukset',
      subtitle: 'Korjaustilaukset ja historia',
      action: () => onMenuItemClick('repairs')
    }] : []),
    // Hour Reporting (conditional + feature flag)
    ...(canAccessHourReporting && (isSuperAdmin || tenant?.feature_hour_tracking) ? [{
      id: 'hourReporting',
      icon: Clock,
      label: 'Tuntikirjaus',
      subtitle: 'Työaikakirjaukset',
      action: () => onMenuItemClick('hourReporting')
    }] : []),
    // Diary (conditional + feature flag)
    ...(canAccessDiary && (isSuperAdmin || tenant?.feature_diary) ? [{
      id: 'diary',
      icon: BookOpen,
      label: 'Työmaapäiväkirja',
      subtitle: 'Päivittäiset merkinnät',
      action: () => onMenuItemClick('diary')
    }] : []),
    // CRM (conditional + feature flag)
    ...((isSuperAdmin || tenant?.feature_crm) ? [{
      id: 'crm',
      icon: Users,
      label: 'CRM',
      subtitle: 'Asiakashallinta ja myynti',
      action: () => onMenuItemClick('crm')
    }] : []),
    // Reports (conditional)
    ...(canAccessReports ? [{
      id: 'reports',
      icon: BarChart3,
      label: 'Raportit',
      subtitle: 'Analytiikka ja tilastot',
      action: () => onMenuItemClick('reports'),
      highlight: true
    }] : []),
    // Admin Dashboard (conditional)
    ...(canAccessAdminPanel ? [{
      id: 'admin',
      icon: Shield,
      label: 'Admin Dashboard',
      subtitle: 'Hallinnoi järjestelmää',
      action: () => onMenuItemClick('admin'),
      highlight: true
    }] : []),
    // External Links divider (only if there are links)
    ...(externalLinks.length > 0 ? [{ divider: true, id: 'external-divider' }] : []),
    // External Links
    ...externalLinks.map(link => ({
      id: `external-${link.id}`,
      icon: ExternalLink,
      label: link.title,
      subtitle: link.url.replace(/^https?:\/\//, '').split('/')[0],
      emoji: link.icon,
      action: () => handleExternalLinkClick(link),
      isExternal: true
    })),
    // Divider
    { divider: true },
    // Logout
    {
      id: 'logout',
      icon: LogOut,
      label: 'Kirjaudu ulos',
      subtitle: 'Poistu järjestelmästä',
      action: () => onMenuItemClick('logout'),
      danger: true,
      alwaysShow: true
    }
  ];

  // Filter out items that are in the bottom nav (unless alwaysShow is true)
  const menuItems = allMenuItems.filter(item =>
    item.divider || item.alwaysShow || !hiddenItems.includes(item.id)
  );

  // Remove divider if it would be at the start or have nothing before it
  const cleanedMenuItems = menuItems.filter((item, index) => {
    if (item.divider) {
      // Check if there's at least one non-profile item before this divider
      const itemsBefore = menuItems.slice(0, index).filter(i => !i.divider && i.id !== 'profile');
      return itemsBefore.length > 0;
    }
    return true;
  });

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          zIndex: 9998,
          animation: 'fadeIn 0.3s ease-out'
        }}
      />

      {/* Slide-up menu */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: '#1e293b',
        borderTopLeftRadius: '24px',
        borderTopRightRadius: '24px',
        maxWidth: '800px',
        margin: '0 auto',
        maxHeight: '80vh',
        overflow: 'auto',
        zIndex: 9999,
        animation: 'slideUp 0.3s ease-out',
        boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.5)'
      }}>
        {/* Handle bar */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          padding: '12px 0 8px 0',
          position: 'sticky',
          top: 0,
          background: '#1e293b',
          zIndex: 1
        }}>
          <div style={{
            width: '40px',
            height: '5px',
            background: '#475569',
            borderRadius: '3px'
          }} />
        </div>

        {/* Header */}
        <div style={{
          padding: '0 20px 16px 20px',
          borderBottom: '1px solid #334155',
          position: 'sticky',
          top: '28px',
          background: '#1e293b',
          zIndex: 1
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h2 style={{
              fontSize: '20px',
              fontWeight: 'bold',
              color: 'white',
              margin: 0
            }}>
              Valikko
            </h2>
            <button
              onClick={onClose}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                background: '#334155',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.background = '#475569'}
              onMouseOut={(e) => e.currentTarget.style.background = '#334155'}
            >
              <X size={20} color="white" />
            </button>
          </div>
        </div>

        {/* Menu items */}
        <div style={{ padding: '12px 20px 32px 20px' }}>
          {cleanedMenuItems.map((item, index) => {
            if (item.divider) {
              return (
                <div
                  key={`divider-${index}`}
                  style={{
                    height: '1px',
                    background: '#334155',
                    margin: '12px 0'
                  }}
                />
              );
            }

            const Icon = item.icon;

            return (
              <button
                key={item.id}
                onClick={() => {
                  item.action();
                  if (!item.isExternal) {
                    onClose();
                  }
                }}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  padding: '16px',
                  background: item.highlight
                    ? 'rgba(255, 235, 59, 0.1)'
                    : item.isExternal
                      ? 'rgba(139, 92, 246, 0.05)'
                      : 'transparent',
                  border: item.highlight
                    ? '1px solid rgba(255, 235, 59, 0.3)'
                    : item.isExternal
                      ? '1px solid rgba(139, 92, 246, 0.2)'
                      : 'none',
                  borderRadius: '12px',
                  marginBottom: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  textAlign: 'left'
                }}
                onMouseOver={(e) => {
                  if (item.highlight) {
                    e.currentTarget.style.background = 'rgba(255, 235, 59, 0.15)';
                  } else if (item.danger) {
                    e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                  } else if (item.isExternal) {
                    e.currentTarget.style.background = 'rgba(139, 92, 246, 0.1)';
                  } else {
                    e.currentTarget.style.background = '#242d3f';
                  }
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = item.highlight
                    ? 'rgba(255, 235, 59, 0.1)'
                    : item.isExternal
                      ? 'rgba(139, 92, 246, 0.05)'
                      : 'transparent';
                }}
              >
                {/* Icon */}
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: item.danger
                    ? 'rgba(239, 68, 68, 0.1)'
                    : item.highlight
                      ? 'rgba(255, 235, 59, 0.2)'
                      : item.isExternal
                        ? 'rgba(139, 92, 246, 0.15)'
                        : '#334155',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  fontSize: item.emoji ? '24px' : undefined
                }}>
                  {item.emoji ? (
                    item.emoji
                  ) : (
                    <Icon
                      size={24}
                      color={item.danger ? '#ef4444' : item.highlight ? '#FFEB3B' : item.isExternal ? '#8b5cf6' : '#94a3b8'}
                    />
                  )}
                </div>

                {/* Text */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: item.danger ? '#ef4444' : 'white',
                    marginBottom: '2px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    {item.label}
                    {item.isExternal && (
                      <ExternalLink size={14} color="#8b5cf6" />
                    )}
                  </div>
                  {item.subtitle && (
                    <div style={{
                      fontSize: '13px',
                      color: item.isExternal ? '#8b5cf6' : '#94a3b8',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {item.subtitle}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* External Link Warning Dialog */}
      {externalLinkWarning && (
        <>
          <div
            onClick={() => setExternalLinkWarning(null)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.8)',
              zIndex: 10000,
              animation: 'fadeIn 0.2s ease-out'
            }}
          />
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: '#1e293b',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '400px',
            width: 'calc(100% - 40px)',
            zIndex: 10001,
            border: '2px solid #f59e0b',
            animation: 'fadeIn 0.2s ease-out'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '16px'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(245, 158, 11, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <AlertTriangle size={28} color="#f59e0b" />
              </div>
              <div>
                <h3 style={{ margin: 0, color: '#f59e0b', fontSize: '18px', fontWeight: '700' }}>
                  Poistumassa OneTrackista
                </h3>
              </div>
            </div>

            <p style={{
              color: '#cbd5e1',
              fontSize: '14px',
              lineHeight: '1.6',
              margin: '0 0 12px 0'
            }}>
              Olet siirtymässä ulkoiseen sivustoon:
            </p>

            <div style={{
              background: '#0f172a',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span style={{ fontSize: '20px' }}>{externalLinkWarning.icon || '🔗'}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: 'white', fontWeight: '600', fontSize: '14px' }}>
                  {externalLinkWarning.title}
                </div>
                <div style={{
                  color: '#64748b',
                  fontSize: '12px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {externalLinkWarning.url}
                </div>
              </div>
            </div>

            <p style={{
              color: '#94a3b8',
              fontSize: '13px',
              lineHeight: '1.5',
              margin: '0 0 20px 0'
            }}>
              Linkki avautuu uuteen välilehteen. OneTrack ei vastaa ulkoisten sivustojen sisällöstä.
            </p>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setExternalLinkWarning(null)}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  background: '#334155',
                  color: '#e2e8f0',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Peruuta
              </button>
              <button
                onClick={confirmExternalLink}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  background: '#8b5cf6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}
              >
                Jatka sivulle
                <ExternalLink size={16} />
              </button>
            </div>
          </div>
        </>
      )}

      {/* Animations */}
      <style>{`
        @keyframes slideUp {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
      `}</style>
    </>
  );
}

export default SlideUpMenu;
